/**
 * Pecinhas de tela usadas em vários lugares:
 * Indicador (número de destaque), Aviso (mensagem com ícone e texto),
 * SeletorMes (‹ outubro de 2026 ›), ErroNaTela (erro da API com código) e
 * TrocaDePainel (Aliança | Bauny).
 *
 * Lê: só as props.
 * Usado por: telas/*.tsx.
 */
import type { ReactNode } from 'react';
import type { ErroApi } from '../dados/api.ts';
import { mesAtual, mesesDepois } from '../dominio/datas.ts';
import type { Fonte, Mes } from '../dominio/tipos.ts';
import { NOME_DO_PAINEL, NOME_DO_SISTEMA, nomeDoMes } from '../formatacao.ts';

export function Indicador({ rotulo, valor, detalhe, principal = false }: { rotulo: string; valor: string; detalhe?: ReactNode; principal?: boolean }) {
  return (
    <div className={principal ? 'indicador indicador-principal' : 'indicador'}>
      <span className="indicador-rotulo">{rotulo}</span>
      <span className="indicador-valor">{valor}</span>
      {detalhe ? <span className="indicador-detalhe">{detalhe}</span> : null}
    </div>
  );
}

const ICONES = { info: 'ℹ', alerta: '!', erro: '×', ok: '✓' } as const;
const ROTULOS = { info: 'Informação', alerta: 'Atenção', erro: 'Erro', ok: 'Pronto' } as const;

/** Cor nunca vem sozinha: todo aviso tem ícone e uma palavra (Atenção, Erro...). */
export function Aviso({ tipo, children }: { tipo: keyof typeof ICONES; children: ReactNode }) {
  return (
    <div className={`aviso aviso-${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      <span className="aviso-icone" aria-hidden="true">
        {ICONES[tipo]}
      </span>
      <div>
        <strong className="aviso-rotulo">{ROTULOS[tipo]}: </strong>
        {children}
      </div>
    </div>
  );
}

export function SeletorMes({ mes, aoMudar }: { mes: Mes; aoMudar: (mes: Mes) => void }) {
  const atual = mesAtual();
  return (
    <div className="seletor-mes">
      <button type="button" className="botao-icone" aria-label="Mês anterior" onClick={() => aoMudar(mesesDepois(mes, -1))}>
        ‹
      </button>
      <span className="seletor-mes-nome">{nomeDoMes(mes)}</span>
      <button type="button" className="botao-icone" aria-label="Próximo mês" onClick={() => aoMudar(mesesDepois(mes, 1))}>
        ›
      </button>
      {mes !== atual ? (
        <button type="button" className="botao-link" onClick={() => aoMudar(atual)}>
          voltar para o mês atual
        </button>
      ) : null}
    </div>
  );
}

export function ErroNaTela({ erro }: { erro: ErroApi | Error }) {
  const codigo = 'codigo' in erro ? (erro as ErroApi).codigo : null;
  const pedido = 'pedido' in erro ? (erro as ErroApi).pedido : null;
  return (
    <Aviso tipo="erro">
      {erro.message}
      {codigo ? <span className="codigo-erro"> [{codigo}{pedido ? ` · pedido ${pedido}` : ''}]</span> : null}
    </Aviso>
  );
}

/** Os dois painéis, lado a lado no topo da tela. */
export function TrocaDePainel({ ativo, link }: { ativo: Fonte; link: (fonte: Fonte) => string }) {
  return (
    <nav className="troca-painel" aria-label="Painel">
      {(['winthor', 'sap-bauny'] as const).map((fonte) => (
        <a key={fonte} href={link(fonte)} aria-current={fonte === ativo ? 'page' : undefined}>
          {NOME_DO_PAINEL[fonte]} <small>({NOME_DO_SISTEMA[fonte]})</small>
        </a>
      ))}
    </nav>
  );
}
